import React, { ReactElement, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { splitGamesIntoUnits } from './grid-utils';
import { isSmallerSelector } from '~/components/layout/mediaquery';
import { GridsContainer, Grid, CardContainer } from './grid.styles';
import GameCardBottomInfo from '~/components/casino-shared/game-card/game-card-bottom-info';
import styled from 'styled-components';
import breakpoint from '~/utils/style-utils';
import { EnhancedGame } from '~/components/casino-shared/types';

const CardContainerWithBottomInfo = styled(CardContainer)`
	flex-direction: column;
	flex-shrink: 0;
`;

// this container will fit cards to the view without separating them 3x ("view all" category items button's result)
const NoGridUnitContainer = styled.div<{ cardWidth: string }>`
	display: grid;
	justify-content: center;
	align-items: center;
	grid-template-columns: ${({ cardWidth }) => `repeat(auto-fit, minmax(${cardWidth}, 1fr));`};
	gap: 12px;
	justify-items: center;
	padding: 12px;

	${({ cardWidth }) => breakpoint.lg`
    	padding: 12px 24px 56px 24px;
			grid-template-columns: ${`repeat(auto-fill, ${cardWidth});`};
			justify-content: start;
  `}
`;

const cardPositions = {
	1: `
		grid-column: 1;
		grid-row: 1;
	`,
	2: `
		grid-column: 2;
		grid-row: 1;
	`,
	3: `
		grid-column: 3;
		grid-row: 1;
	`
};

type GridType3Props = {
	allowIncompleteUnits?: boolean;
	gridUnitSupport?: boolean;
};

const GridType3 = <
	T extends ReactElement<{
		game?: { name: EnhancedGame['name']; provider: EnhancedGame['provider'] };
		showLiveFeed?: boolean;
	}>[]
>({
	GameCards,
	allowIncompleteUnits = false,
	gridUnitSupport = true
}: { GameCards: T } & GridType3Props) => {
	const isSmaller = useSelector(isSmallerSelector);

	const itemsPerUnit = 3;

	const units = useMemo(
		() => splitGamesIntoUnits(GameCards, itemsPerUnit, allowIncompleteUnits),
		[GameCards, itemsPerUnit, allowIncompleteUnits]
	);

	const cardSizes = useMemo(
		() => ({
			width: isSmaller ? '162px' : '220px',
			height: `calc(${isSmaller ? 162 : 220}px + var(--gameCardBottomInfoHeight))`
		}),
		[isSmaller]
	);

	const gridSizes = useMemo(
		() => ({
			width: isSmaller ? '510px' : '684px',
			height: `calc(${isSmaller ? 162 : 220}px + var(--gameCardBottomInfoHeight))`
		}),
		[isSmaller]
	);

	const cardMapper = (card: T[number], cardIndex: number) => {
		return (
			<CardContainerWithBottomInfo
				key={card.key}
				size={cardSizes}
				position={gridUnitSupport && cardPositions[(cardIndex + 1) as keyof typeof cardPositions]}
			>
				{React.cloneElement(card, {
					showLiveFeed: true
				})}
				<GameCardBottomInfo game={'game' in card.props ? card?.props?.game : undefined} />
			</CardContainerWithBottomInfo>
		);
	};

	if (!gridUnitSupport) {
		return (
			<NoGridUnitContainer cardWidth={cardSizes.width}>
				{GameCards.map((card, cardIndex) => cardMapper(card, cardIndex))}
			</NoGridUnitContainer>
		);
	}

	return (
		<GridsContainer>
			{units.map((cards, unitIndex) => (
				<Grid
					// eslint-disable-next-line react/no-array-index-key
					key={`unit-${unitIndex}`}
					// check allowIncompleteUnits prop to set width to auto to provide
					// correct view in continue_playing type with fewer items than nrMinimumItems
					width={allowIncompleteUnits ? 'auto' : gridSizes.width}
					height={gridSizes.height}
				>
					{cards.map((card, cardIndex) => cardMapper(card, cardIndex))}
				</Grid>
			))}
		</GridsContainer>
	);
};

export default GridType3;
